/**
 * @fileoverview A program viewer's Settings tab: what they hear about this
 * program, who to ask about it, and leaving it.
 *
 * - **Emails** — the weekly recap and week-ahead emails the organization
 *   granted, each one the viewer can switch off (and back on). An email they
 *   were not granted is not theirs to switch on, so it is not offered.
 * - **Questions about this program** — the people the organization named as
 *   the program's contacts, or its administrators, by email.
 * - **What's shared with you** — their grants, in words. Only the
 *   organization changes them.
 * - **Leave this program** — removes the program from what they can see. From
 *   their last program it closes the viewer account and signs them out; the
 *   organization can always add them again.
 *
 * Read from and saved to `/org/viewer/programs/{id}/settings`; leaving is
 * `DELETE /org/viewer/programs/{id}`.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, ButtonLink } from "@/components/studio/primitives";
import { SettingsRow, SettingsSection, Toggle } from "@/components/settings/SettingsSection";
import { permissionLabels } from "@/components/org/AddViewerDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/** The grants that are emails rather than parts of the page. */
const EMAIL_GRANTS = ["weekly_recap", "week_ahead"];

/**
 * @param {{ programId: string, programTitle: string, orgName: string,
 *   onLeft: (result: {account_closed?: boolean}) => void }} props
 */
export default function ViewerSettings({ programId, programTitle, orgName, onLeft }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const org = orgName || settings?.org_name || "The organization";

  useEffect(() => {
    let live = true;
    orgApi
      .get(`/org/viewer/programs/${programId}/settings`)
      .then((res) => live && setSettings(res.data))
      .catch(() => live && setSettings({ failed: true }));
    return () => {
      live = false;
    };
  }, [programId]);

  const setEmail = async (id, on) => {
    setSaving(id);
    const before = settings;
    setSettings((s) => ({ ...s, emails: s.emails.map((e) => (e.id === id ? { ...e, on } : e)) }));
    try {
      const res = await orgApi.put(`/org/viewer/programs/${programId}/settings`, { emails: { [id]: on } });
      setSettings(res.data);
    } catch (err) {
      setSettings(before);
      toast.error(getOrgErrorMessage(err, "Couldn't save that"));
    } finally {
      setSaving(null);
    }
  };

  const leave = async () => {
    setLeaving(true);
    try {
      const res = await orgApi.delete(`/org/viewer/programs/${programId}`);
      onLeft(res.data || {});
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't remove you from this program"));
      setLeaving(false);
    }
  };

  if (!settings) return <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</p>;
  if (settings.failed) return <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Couldn&rsquo;t load your settings.</p>;

  const emails = (settings.emails || []).filter((e) => e.granted);
  const contacts = settings.contacts || [];
  const shared = permissionLabels((settings.permissions || []).filter((p) => !EMAIL_GRANTS.includes(p)));

  return (
    <div className="flex max-w-3xl flex-col gap-6" data-testid="viewer-settings">
      <SettingsSection title="Emails" testId="viewer-settings-emails">
        {emails.length === 0 ? (
          <SettingsRow last desc={`${org} hasn't added you to this program's emails.`} />
        ) : (
          emails.map((e, i) => (
            <SettingsRow
              key={e.id}
              last={i === emails.length - 1}
              title={e.label}
              desc={e.desc}
              testId={`viewer-email-${e.id}`}
              trailing={<Toggle on={e.on} onChange={(on) => setEmail(e.id, on)} disabled={saving === e.id} label={e.label} testId={`viewer-email-toggle-${e.id}`} />}
            />
          ))
        )}
      </SettingsSection>

      <SettingsSection title="Questions about this program" testId="viewer-settings-contacts">
        {contacts.length === 0 ? (
          <SettingsRow last desc={`${org} hasn't named a contact for this program yet.`} />
        ) : (
          contacts.map((c, i) => (
            <SettingsRow
              key={c.email || c.name}
              last={i === contacts.length - 1}
              title={c.name}
              desc={[c.role, c.email, c.phone].filter(Boolean).join(" · ")}
              testId={`viewer-contact-${i}`}
              trailing={
                c.email ? (
                  <ButtonLink
                    href={`mailto:${c.email}?subject=${encodeURIComponent(programTitle || "Your program")}`}
                    icon="mail"
                    data-testid={`viewer-contact-email-${i}`}
                  >
                    Email {c.name?.split(" ")[0] || "them"}
                  </ButtonLink>
                ) : undefined
              }
            />
          ))
        )}
      </SettingsSection>

      <SettingsSection title="What's shared with you" testId="viewer-settings-shared">
        <SettingsRow
          last
          title={shared.length ? shared.join(", ") : "Nothing beyond the classes and calendar"}
          desc={`Every viewer sees the program's classes and calendar. Only ${org} can change what else you see, so ask them if you need more, or less.`}
        />
      </SettingsSection>

      <SettingsSection title="Leave this program" testId="viewer-settings-leave">
        <SettingsRow
          last
          title="Remove me as a viewer"
          desc={`You'll stop seeing ${programTitle || "this program"} and stop getting its emails. ${org} can add you again.`}
          trailing={
            <Button variant="danger" size="sm" onPress={() => setConfirming(true)} data-testid="viewer-leave">
              Leave program
            </Button>
          }
        />
      </SettingsSection>

      <AlertDialog open={confirming} onOpenChange={(v) => !leaving && setConfirming(v)}>
        <AlertDialogContent className="mx-4 max-w-[calc(100%-2rem)] border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)] text-[var(--text-primary)] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Leave {programTitle || "this program"}?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--text-secondary)]">
              You&rsquo;ll lose access to it and its emails straight away. If it&rsquo;s the only program shared with
              you, your viewer account closes and you&rsquo;re signed out.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] text-[var(--text-primary)]">
              Stay
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                leave();
              }}
              disabled={leaving}
              className="bg-[var(--state-danger)] text-[var(--text-on-state)]"
              data-testid="viewer-leave-confirm"
            >
              {leaving ? "Leaving…" : "Leave program"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
